import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Box,
  Link,
  Typography,
  InputLabel,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CompactPicker } from 'react-color';
import { QRCode } from 'react-qrcode-logo';
import {
  ColorLens as ColorIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  GridOn as DotsIcon,
  GridView as SquaresIcon,
  Waves as FluidIcon,
  CropSquare as SquareEyeIcon,
  PanoramaFishEye as RoundEyeIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Crop54Outlined as Crop54OutlinedIcon,
  OpenInNew as OpenInNewIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import QRCodeStore from 'store/QRCodeStore';
import M from 'messages';
import classes from './styles';

const QRCodeModal = observer(
  ({
    open,
    handleClose,
    handleSave,
    dialogTitle = '',
    dialogDescription = '',
    dialogLinkTitle = '',
    warningMessage = '',
  }) => {
    const [showQrColorPicker, setShowQrColorPicker] = useState(false);
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [qrRef, setQrRef] = useState(null);
    const qrData = QRCodeStore.getQRData();
    const { liveStatus } = qrData;
    const isLive = liveStatus === 'live';

    const qrColorRef = useRef(null);
    const bgColorRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = event => {
        if (qrColorRef.current && !qrColorRef.current.contains(event.target)) {
          setShowQrColorPicker(false);
        }
        if (bgColorRef.current && !bgColorRef.current.contains(event.target)) {
          setShowBgColorPicker(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleLogoUpload = e => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        QRCodeStore.onChangeField('qrLogo', reader.result);
      };
      reader.readAsDataURL(file);
    };

    const handleDownload = () => {
      const canvas = qrRef.querySelector('canvas');
      const img = canvas.toDataURL();
      const link = document.createElement('a');
      link.href = img;
      link.download = 'qr-code.png';
      link.click();
    };

    const handleSaveAsync = async () => {
      setSaveLoading(true);
      await handleSave();
      setSaveLoading(false);
    };

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle style={{ padding: '16px 24px 8px' }}>{dialogTitle}</DialogTitle>
        <DialogContent style={{ padding: '0 24px 8px' }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }} mt={0}>
              <Typography variant="body2">
                {isLive ? M.get('wineLists.qr.liveStatus.live.description') : dialogDescription}
              </Typography>
            </Grid>

            {liveStatus && (
              <Grid size={{ xs: 12 }}>
                <Alert severity={isLive ? 'success' : 'warning'}>
                  <AlertTitle>{M.get(`wineLists.qr.liveStatus.${liveStatus}.title`)}</AlertTitle>
                  {M.get(`wineLists.qr.liveStatus.${liveStatus}.text`)}
                </Alert>
              </Grid>
            )}

            {/* Customization Tools on the Left */}
            <Grid size={{ xs: 6 }}>
              {/* QR Code Style Selection with Icons */}
              <Box>
                <Typography variant="subtitle1">{M.get('wineLists.qr.qrStyle')}</Typography>
                <Grid container spacing={1}>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.dotsStyle')}>
                      <IconButton
                        onClick={() => QRCodeStore.onChangeField('qrStyle', 'dots')}
                        color={qrData.qrStyle === 'dots' ? 'primary' : 'default'}>
                        <DotsIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.squaresStyle')}>
                      <IconButton
                        onClick={() => QRCodeStore.onChangeField('qrStyle', 'squares')}
                        color={qrData.qrStyle === 'squares' ? 'primary' : 'default'}>
                        <SquaresIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.fluidStyle')}>
                      <IconButton
                        onClick={() => QRCodeStore.onChangeField('qrStyle', 'fluid')}
                        color={qrData.qrStyle === 'fluid' ? 'primary' : 'default'}>
                        <FluidIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>

              {/* QR Code Eye/Corners Style Selection with Icons */}
              <Box>
                <Typography variant="subtitle1">{M.get('wineLists.qr.cornersStyle')}</Typography>
                <Grid container spacing={1}>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.squareCorners')}>
                      <IconButton
                        onClick={() =>
                          QRCodeStore.onChangeField('qrEyeStyle', [
                            { outer: [0, 0, 0, 0], inner: [0, 0, 0, 0] },
                            { outer: [0, 0, 0, 0], inner: [0, 0, 0, 0] },
                            { outer: [0, 0, 0, 0], inner: [0, 0, 0, 0] },
                          ])
                        }
                        color={qrData.qrEyeStyle[0].outer.every(val => val === 0) ? 'primary' : 'default'}>
                        <SquareEyeIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.roundedSquareCorners')}>
                      <IconButton
                        onClick={() =>
                          QRCodeStore.onChangeField('qrEyeStyle', [
                            { outer: [10, 10, 10, 10], inner: [10, 10, 10, 10] },
                            { outer: [10, 10, 10, 10], inner: [10, 10, 10, 10] },
                            { outer: [10, 10, 10, 10], inner: [10, 10, 10, 10] },
                          ])
                        }
                        color={qrData.qrEyeStyle[0].outer.every(val => val === 10) ? 'primary' : 'default'}>
                        <Crop54OutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title={M.get('wineLists.qr.roundCorners')}>
                      <IconButton
                        onClick={() =>
                          QRCodeStore.onChangeField('qrEyeStyle', [
                            { outer: [50, 50, 50, 50], inner: [50, 50, 50, 50] },
                            { outer: [50, 50, 50, 50], inner: [50, 50, 50, 50] },
                            { outer: [50, 50, 50, 50], inner: [50, 50, 50, 50] },
                          ])
                        }
                        color={qrData.qrEyeStyle[0].outer.every(val => val === 50) ? 'primary' : 'default'}>
                        <RoundEyeIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>

              {/* QR Code Color Picker */}
              <Box>
                <Typography variant="subtitle1">{M.get('wineLists.qr.qrCodeColor')}</Typography>
                <Box>
                  <Tooltip title={M.get('wineLists.qr.changeQrColor')} arrow>
                    <IconButton
                      onClick={() => setShowQrColorPicker(!showQrColorPicker)}
                      sx={classes.colorButton(qrData.qrColor)}>
                      <ColorIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={M.get('wineLists.qr.resetQrColor')} arrow>
                    <IconButton onClick={() => QRCodeStore.onChangeField('qrColor', '#000000')} color="default">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  {showQrColorPicker && (
                    <Box ref={qrColorRef} sx={classes.colorPicker}>
                      <CompactPicker
                        color={qrData.qrColor}
                        onChange={color => QRCodeStore.onChangeField('qrColor', color.hex)}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Background Color Picker */}
              <Box>
                <Typography variant="subtitle1">{M.get('wineLists.qr.backgroundColor')}</Typography>
                <Box>
                  <Tooltip title={M.get('wineLists.qr.changeBackgroundColor')} arrow>
                    <IconButton
                      onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                      sx={classes.colorButton(qrData.qrBackgroundColor)}>
                      <ColorIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={M.get('wineLists.qr.resetBackgroundColor')} arrow>
                    <IconButton
                      onClick={() => QRCodeStore.onChangeField('qrBackgroundColor', '#ffffff')}
                      color="default">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  {showBgColorPicker && (
                    <Box ref={bgColorRef} sx={classes.colorPicker}>
                      <CompactPicker
                        color={qrData.qrBackgroundColor}
                        onChange={color => QRCodeStore.onChangeField('qrBackgroundColor', color.hex)}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Upload Logo */}
              <Box>
                <InputLabel>{M.get('wineLists.qr.uploadLogoOptional')}</InputLabel>
                <Box mt={1} sx={classes.uploadContainer}>
                  <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                    {M.get('wineLists.qr.uploadLogo')}
                    <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                  </Button>
                  {qrData.qrLogo && (
                    <Tooltip title={M.get('wineLists.qr.deleteLogo')} arrow>
                      <IconButton onClick={() => QRCodeStore.onChangeField('qrLogo', null)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* QR Code and URL on the Right */}
            <Grid size={{ xs: 6 }}>
              <Box textAlign="center">
                <div ref={setQrRef}>
                  <QRCode
                    ecLevel="H"
                    value={qrData.qrUrl}
                    logoImage={qrData.qrLogo}
                    fgColor={qrData.qrColor}
                    bgColor={qrData.qrBackgroundColor}
                    size={220}
                    logoWidth={50}
                    logoHeight={50}
                    logoOpacity={1}
                    qrStyle={qrData.qrStyle}
                    logoPaddingStyle="circle"
                    logoPadding={2}
                    eyeRadius={qrData.qrEyeStyle}
                  />
                </div>
              </Box>
              {liveStatus ? (
                <Box textAlign="center">
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<OpenInNewIcon />}
                    disabled={!isLive}
                    href={qrData.qrRedirectUrl}
                    target="_blank"
                    rel="noopener"
                    sx={classes.openLinkButton}>
                    {M.get('wineLists.qr.openLink')}
                  </Button>
                  {isLive ? (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      {M.get('wineLists.qr.liveHint')}
                    </Typography>
                  ) : (
                    <Box mt={1} sx={classes.notLiveHint}>
                      <Chip
                        icon={<ClockIcon />}
                        label={M.get('wineLists.qr.notLiveChip')}
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary" textAlign="left">
                        {M.get(`wineLists.qr.liveStatus.${liveStatus}.hint`)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box mt={1} textAlign="center" alignItems="center">
                  <Typography variant="subtitle1">{dialogLinkTitle}</Typography>
                  <Link
                    href={qrData.qrRedirectUrl}
                    target="_blank"
                    rel="noopener"
                    variant="body1"
                    underline="hover"
                    sx={classes.qrRedirectUrl}>
                    {qrData.qrRedirectUrl}
                  </Link>
                </Box>
              )}
            </Grid>
          </Grid>
          {warningMessage && (
            <Grid size={{ xs: 12 }} mt={2}>
              {warningMessage}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={classes.actionButtons}>
          <Button onClick={handleClose} color="primary" sx={classes.actionButton}>
            {M.get('actions.discard')}
          </Button>
          <Button onClick={handleDownload} startIcon={<DownloadIcon />} sx={classes.actionButton} variant="contained">
            {M.get('actions.download')}
          </Button>
          <Button
            onClick={handleSaveAsync}
            color="primary"
            sx={classes.actionButton}
            variant="contained"
            disabled={saveLoading}>
            {saveLoading ? <CircularProgress size={16} /> : M.get('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

QRCodeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
};

export default QRCodeModal;
